import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Button,
  Autocomplete,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  Typography,
  IconButton,
  Divider,
  Chip,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Paper,
  Alert,
  FormHelperText,
  CircularProgress,
  Zoom,
  styled,
  alpha,
  useTheme,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  Person,
  Phone,
  DirectionsCar,
  LocalParking,
  Event,
  Euro,
  Add,
  Close,
  ArrowForward,
  ArrowBack,
  CalendarMonth,
  Group,
  ChildCare,
  Check,
  Payment,
  CreditCard,
  AccountBalance,
  AttachMoney,
  EventAvailable,
  Warning,
  Schedule,
  Note,
} from '@mui/icons-material';

// Import statement for Info icon at the top
import { Info } from '@mui/icons-material';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 12,
    overflow: 'hidden',
    maxWidth: 700,
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
    theme.palette.primary.main,
    0.1
  )} 100%)`,
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  padding: theme.spacing(2, 3),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const PaymentMethodButton = styled(Button)(({ theme, active }) => ({
  borderColor: active ? theme.palette.primary.main : alpha(theme.palette.divider, 0.5),
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: active 
      ? alpha(theme.palette.primary.main, 0.15) 
      : alpha(theme.palette.action.hover, 0.1),
  },
  padding: theme.spacing(1, 2),
  width: '100%',
  justifyContent: 'flex-start',
  transition: 'all 0.2s',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  fontWeight: 500,
  borderRadius: 4,
  '& .MuiChip-icon': {
    color: theme.palette.primary.main,
  },
}));

const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.info.main, 0.1),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  fontWeight: 500,
  transition: 'all 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

// Utility function to get current date and time in the format YYYY-MM-DDTHH:mm
const getCurrentDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Check if two bookings overlap
const isOverlapping = (newStart, newEnd, existingStart, existingEnd) => {
  if (newStart >= existingStart && newStart < existingEnd) return true;
  if (newEnd > existingStart && newEnd <= existingEnd) return true;
  if (newStart <= existingStart && newEnd >= existingEnd) return true;
  return false;
};

// Validate start date
const validateStartDate = (value) => {
  return dayjs(value).isValid() || 'Data e Ora di Inizio non validi';
};

const BookingModal = ({
  open,
  booking,
  users,
  addUser,
  addVehicle,
  pitches,
  vehicles,
  stops,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      userId: 'new',
      firstName: booking ? booking.users.firstName : '',
      lastName: booking ? booking.users.lastName : '',
      phone: booking ? booking.users.phone : '',
      prefix: booking ? booking.users.prefix : '+39',
      pitch: booking ? booking.restareas.friendlyName : '',
      pitchId: booking ? booking.restareas.id : '',
      start: booking
        ? new Date(booking.start * 1000).toISOString().slice(0, 16)
        : getCurrentDateTime(),
      duration: booking ? (booking.end - booking.start) / (24 * 60 * 60) : 1,
      price: booking ? booking.price : 0,
      vehicle: booking ? booking.vehicles.licensePlate : '',
      vehicleId: booking ? booking.vehicles.id : '',
      licensePlate: booking ? booking.vehicles.licensePlate : '',
      pricePerDay: booking ? booking.restareas.price : 0,
      adults: booking ? booking.adults : 1,
      childrens: booking ? booking.childrens : 0,
      restareasId: booking ? booking.restareasId : '',
      stopzonesId: booking ? booking.stopzonesId : '',
      paymentType: booking ? booking.paymentType : 'contanti',
    },
  });

  const [isNewUser, setIsNewUser] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userVehicles, setUserVehicles] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');

  // Watch form values
  const watchStart = watch('start');
  const watchDuration = watch('duration');
  const watchPricePerDay = watch('pricePerDay');
  const watchPaymentType = watch('paymentType');
  const watchPrice = watch('price');

  // Calculate timestamps
  const startUnix = useMemo(() => dayjs(watchStart).unix(), [watchStart]);
  const endUnix = useMemo(() => startUnix + watchDuration * 86400, [startUnix, watchDuration]);
  
  // Calculate stay period in human-readable format
  const stayPeriod = useMemo(() => {
    if (!watchStart || !watchDuration) return '';
    
    const startDate = dayjs(watchStart);
    const endDate = startDate.add(watchDuration, 'day');
    
    return `${startDate.format('DD/MM/YYYY HH:mm')} - ${endDate.format('DD/MM/YYYY HH:mm')}`;
  }, [watchStart, watchDuration]);

  // Steps for the wizard
  const steps = ['Cliente', 'Prenotazione', 'Pagamento'];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!booking) {
      reset({
        userId: 'new',
        firstName: '',
        lastName: '',
        phone: '',
        prefix: '+39',
        pitch: '',
        pitchId: '',
        start: getCurrentDateTime(),
        duration: 1,
        price: 0,
        vehicle: '',
        vehicleId: '',
        licensePlate: '',
        pricePerDay: 0,
        adults: 1,
        childrens: 0,
        restareasId: '',
        stopzonesId: '',
        paymentType: 'contanti',
      });
      setIsNewUser(true);
      setUserVehicles([]);
    } else {
      reset({
        userId: booking.users.id,
        firstName: booking.users.firstName,
        lastName: booking.users.lastName,
        phone: booking.users.phone,
        prefix: booking.users.prefix || '+39',
        pitch: booking.restareas.friendlyName,
        pitchId: booking.restareas.id,
        start: new Date(booking.start * 1000).toISOString().slice(0, 16),
        duration: (booking.end - booking.start) / 86400,
        price: booking.price,
        vehicle: booking.vehicles.licensePlate,
        vehicleId: booking.vehicles.id,
        licensePlate: booking.vehicles.licensePlate,
        pricePerDay: booking.restareas.price,
        adults: booking.adults,
        childrens: booking.childrens,
        restareasId: booking.restareasId,
        stopzonesId: booking.stopzonesId,
        paymentType: booking.paymentType || 'contanti',
      });
      setUserVehicles(
        vehicles.find((v) => v.userId === booking.users.id)?.vehicles || []
      );
      setIsNewUser(false);
    }

    setActiveStep(0);
    setError('');
  }, [booking, reset, vehicles, open]);

  // Calculate price based on duration and price per day
  useEffect(() => {
    const totalPrice = (watchDuration || 0) * (watchPricePerDay || 0);
    setValue('price', totalPrice);
  }, [watchDuration, watchPricePerDay, setValue]);

  // Analyze all pitches with availability info
  const pitchesWithAvailability = useMemo(() => {
    if (!watchStart || !watchDuration) return pitches.map(pitch => ({ ...pitch, isAvailable: true, conflictInfo: null }));

    return pitches.map((pitch) => {
      // Skip current booking's pitch if editing - it's always available
      if (booking && booking.restareasId === pitch.id) {
        return { ...pitch, isAvailable: true, conflictInfo: null };
      }
      
      // Check for overlapping bookings with the selected period
      const overlappingStop = stops.find(
        (stop) =>
          stop.restareasId === pitch.id &&
          isOverlapping(startUnix, endUnix, stop.start, stop.end)
      );
      
      if (overlappingStop) {
        // Found conflict - calculate when it will be available
        const availableFrom = overlappingStop.end;
        const availableDate = new Date(availableFrom * 1000);
        const requestedStartDate = new Date(startUnix * 1000);
        
        // Only show the pitch if it becomes available before or on the requested start date
        if (availableFrom <= startUnix) {
          // Available before our requested start time - treat as available
          return { ...pitch, isAvailable: true, conflictInfo: null };
        }
        
        // Check if it becomes available on the same day as requested start
        const availableDateOnly = new Date(availableDate.getFullYear(), availableDate.getMonth(), availableDate.getDate());
        const requestedDateOnly = new Date(requestedStartDate.getFullYear(), requestedStartDate.getMonth(), requestedStartDate.getDate());
        
        if (availableDateOnly.getTime() <= requestedDateOnly.getTime()) {
          // Available on the same day or before - show it with availability info
          const now = new Date();
          let availabilityText = '';
          
          if (availableDate.toDateString() === now.toDateString()) {
            availabilityText = `Disponibile dalle ${availableDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
          } else {
            availabilityText = `Disponibile dalle ${availableDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
          }
          
          return {
            ...pitch,
            isAvailable: false,
            conflictInfo: {
              availableFrom,
              availabilityText,
              type: 'booking'
            }
          };
        } else {
          // Available after the requested date - don't show this pitch
          return null;
        }
      }
      
      return { ...pitch, isAvailable: true, conflictInfo: null };
    }).filter(pitch => pitch !== null); // Remove pitches that are available after the requested date
  }, [pitches, stops, watchStart, watchDuration, startUnix, endUnix, booking]);

  // Show ALL relevant pitches in dropdown with availability info
  const allPitchesForDropdown = pitchesWithAvailability;

  // Form submission handler
  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    
    const startUnixLocal = new Date(data.start).getTime() / 1000;
    const endUnixLocal = startUnixLocal + data.duration * 86400;

    // Parse integers
    const adults = parseInt(data.adults, 10);
    const childrens = parseInt(data.childrens, 10);

    // User data
    const user = {
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      prefix: data.prefix,
    };

    // Check for overlapping bookings (additional safety check)
    const overlappingStop = stops
      .filter((stop) => {
        // Ignore current booking when editing
        if (booking && stop.id === booking.id) return false;
        return stop.restareasId === data.restareasId;
      })
      .some((stop) =>
        isOverlapping(startUnixLocal, endUnixLocal, stop.start, stop.end)
      );

    if (overlappingStop) {
      setError("La prenotazione si sovrappone con un'altra prenotazione per questa piazzola.");
      setLoading(false);
      return;
    }

    try {
      let userId = data.userId;

      // Create new user if needed
      if (isNewUser) {
        const existingUser = users.find((u) => u.phone === data.phone);
        if (existingUser) {
          setError('Esiste già un utente con lo stesso numero di telefono.');
          setLoading(false);
          return;
        } else {
          const newUser = await addUser(user);
          userId = newUser.id;
        }
      }

      // Handle vehicle
      let vehicleId;

      // If vehicleId is set, use existing vehicle
      if (data.vehicleId) {
        vehicleId = data.vehicleId;
      } else if (data.licensePlate && data.licensePlate.trim() !== '') {
        // If licensePlate is provided, check if it already exists or create new
        const existingVehicle = vehicles
          .find((v) => v.userId === userId)
          ?.vehicles.find(
            (vehicle) => vehicle.licensePlate === data.licensePlate
          );

        if (existingVehicle) {
          vehicleId = existingVehicle.id;
        } else {
          const newVehicle = await addVehicle({
            nationality: 'Italia',
            licensePlate: data.licensePlate,
            carName: 'Camper',
            userId: userId,
          });
          vehicleId = newVehicle.id;
        }
      } else {
        setError('Seleziona un veicolo o inserisci una targa.');
        setLoading(false);
        return;
      }

      // Save booking
      await onSave({
        ...data,
        adults,
        childrens,
        userId,
        vehicleId,
        start: startUnixLocal,
        end: endUnixLocal,
        paymentType: data.paymentType,
      });
      
      // Close modal on success (handled by parent component)
    } catch (error) {
      console.error('Error creating booking:', error);
      setError('Si è verificato un errore durante il salvataggio della prenotazione.');
    } finally {
      setLoading(false);
    }
  };

  // User selection handler
  const handleUserChange = (event, value) => {
    if (!value || value.id === 'new') {
      setIsNewUser(true);
      // Reset user-related fields but keep other values
      setValue('userId', 'new');
      setValue('firstName', '');
      setValue('lastName', '');
      setValue('phone', '');
      setValue('prefix', '+39');
      setValue('vehicleId', '');
      setValue('licensePlate', '');
      setUserVehicles([]);
    } else {
      setIsNewUser(false);
      const user = users.find((u) => u.id === value.id);
      setValue('userId', user.id);
      setValue('firstName', user.firstName);
      setValue('lastName', user.lastName);
      setValue('phone', user.phone);
      setValue('prefix', user.prefix || '+39');
      setUserVehicles(
        vehicles.find((v) => v.userId === user.id)?.vehicles || []
      );
    }
  };

  // Pitch selection handler with automatic time adjustment
  const handlePitchChange = (_, value) => {
    if (value !== null) {
      setValue('pitch', value.friendlyName);
      setValue('pitchId', value.id);
      setValue('restareasId', value.id);
      setValue('stopzonesId', value.stopzonesId);
      setValue('pricePerDay', value.price);
      
      // If pitch is not available, automatically adjust start time
      if (!value.isAvailable && value.conflictInfo) {
        const availableFromTimestamp = value.conflictInfo.availableFrom;
        const availableFrom = new Date(availableFromTimestamp * 1000);
        
        console.log('=== DEBUG AUTO-ADJUST ===');
        console.log(`Timestamp ricevuto: ${availableFromTimestamp}`);
        console.log(`Data originale: ${availableFrom.toString()}`);
        console.log(`Data locale: ${availableFrom.toLocaleString('it-IT')}`);
        console.log(`Ore: ${availableFrom.getHours()}, Minuti: ${availableFrom.getMinutes()}`);
        
        // Crea una nuova data per l'arrotondamento
        const roundedTime = new Date(availableFrom.getTime());
        
        console.log(`Prima dell'arrotondamento: ${roundedTime.toString()}`);
        
        // Se ha minuti o secondi, vai all'ora successiva
        if (roundedTime.getMinutes() > 0 || roundedTime.getSeconds() > 0) {
          console.log('Ha minuti/secondi - aggiungo 1 ora');
          roundedTime.setHours(roundedTime.getHours() + 1);
        } else {
          console.log('Non ha minuti/secondi - mantengo ora corrente');
        }
        
        // Azzera minuti e secondi
        roundedTime.setMinutes(0, 0, 0);
        
        console.log(`Dopo arrotondamento: ${roundedTime.toString()}`);
        console.log(`Ora finale: ${roundedTime.getHours()}:${roundedTime.getMinutes().toString().padStart(2, '0')}`);
        
        // Converte in formato locale senza perdere il fuso orario
        const year = roundedTime.getFullYear();
        const month = String(roundedTime.getMonth() + 1).padStart(2, '0');  
        const day = String(roundedTime.getDate()).padStart(2, '0');
        const hours = String(roundedTime.getHours()).padStart(2, '0');
        const minutes = String(roundedTime.getMinutes()).padStart(2, '0');
        
        const adjustedTime = `${year}-${month}-${day}T${hours}:${minutes}`;
        console.log(`Stringa finale per il campo: ${adjustedTime}`);
        
        setValue('start', adjustedTime);
        console.log('=== FINE DEBUG ===');
      }
    }
  };

  // Vehicle selection handler
  const handleVehicleChange = (_, value) => {
    if (!value) {
      setValue('vehicleId', '');
      return;
    }
    // Set only vehicleId, keep licensePlate independent for new vehicle input
    setValue('vehicleId', value.id);
  };

  // Step navigation handlers
  const handleNext = () => {
    // Validate Step 0 (Cliente) before proceeding
    if (activeStep === 0) {
      const userId = getValues('userId');

      // Check if user selected existing client
      if (userId !== 'new') {
        // Existing user - must have vehicle selected OR new license plate entered
        const vehicleId = getValues('vehicleId');
        const licensePlate = getValues('licensePlate');

        if (!vehicleId && (!licensePlate || licensePlate.trim() === '')) {
          setError('Seleziona un veicolo esistente o inserisci una nuova targa per procedere.');
          return;
        }
      } else {
        // New user - validate all required fields
        const firstName = getValues('firstName');
        const lastName = getValues('lastName');
        const phone = getValues('phone');
        const prefix = getValues('prefix');
        const licensePlate = getValues('licensePlate');

        const missingFields = [];
        if (!firstName || firstName.trim() === '') missingFields.push('Nome');
        if (!lastName || lastName.trim() === '') missingFields.push('Cognome');
        if (!prefix || prefix.trim() === '') missingFields.push('Prefisso');
        if (!phone || phone.trim() === '') {
          missingFields.push('Telefono');
        } else if (!/^[0-9]{8,}$/.test(phone)) {
          setError('Il numero di telefono deve contenere almeno 8 cifre e solo numeri.');
          return;
        }
        if (!licensePlate || licensePlate.trim() === '') missingFields.push('Targa Veicolo');

        if (missingFields.length > 0) {
          setError(`Compila tutti i campi obbligatori: ${missingFields.join(', ')}`);
          return;
        }
      }
    }

    // Clear any previous error
    setError('');
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // Payment method icon selector
  const getPaymentIcon = (method) => {
    switch (method) {
      case 'bonifico':
        return <AccountBalance />;
      case 'contanti':
        return <AttachMoney />;
      case 'carta':
        return <CreditCard />;
      default:
        return <Payment />;
    }
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 300 }}
    >
      <StyledDialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {booking ? 'Modifica Prenotazione' : 'Nuova Prenotazione'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </StyledDialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel 
          sx={{ mt: 1, mb: 3 }}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel 
                StepIconProps={{
                  sx: { 
                    '&.Mui-completed': { color: 'success.main' },
                    '&.Mui-active': { color: 'primary.main' },
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        <Box component="form">
          {/* Step 1: Client Data */}
          {activeStep === 0 && (
            <>
              <SectionTitle variant="subtitle1">
                <Person /> Seleziona Cliente
              </SectionTitle>
              
              <Controller
                name="userId"
                control={control}
                rules={{ required: 'Utente obbligatorio' }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    options={[
                      {
                        id: 'new',
                        firstName: 'Crea',
                        lastName: 'Nuovo Cliente',
                        phone: '',
                      },
                      ...users,
                    ]}
                    getOptionLabel={(option) =>
                      option?.id === 'new'
                        ? option.firstName + ' ' + option.lastName
                        : `${option?.firstName || ''} ${option?.lastName || ''} (${option?.phone || ''})`
                    }
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    onChange={handleUserChange}
                    value={
                      users.find((u) => u.id === getValues('userId')) || {
                        id: 'new',
                        firstName: 'Crea',
                        lastName: 'Nuovo Cliente',
                        phone: '',
                      }
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente"
                        fullWidth
                        margin="dense"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {option.id === 'new' ? (
                            <>
                              <Add 
                                fontSize="small" 
                                sx={{ 
                                  mr: 1, 
                                  color: 'primary.main', 
                                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                  borderRadius: '50%',
                                  p: 0.5
                                }} 
                              />
                              <Typography color="primary.main" fontWeight={500}>
                                Crea Nuovo Cliente
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Person fontSize="small" sx={{ mr: 1 }} />
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {option.firstName} {option.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.phone}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </li>
                    )}
                  />
                )}
              />
              
              {isNewUser ? (
                <StyledPaper elevation={0}>
                  <SectionTitle variant="subtitle1">
                    <Person /> Informazioni Nuovo Cliente
                  </SectionTitle>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="firstName"
                        control={control}
                        rules={{ required: 'Nome obbligatorio' }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Nome"
                            fullWidth
                            margin="dense"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person color="primary" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="lastName"
                        control={control}
                        rules={{ required: 'Cognome obbligatorio' }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Cognome"
                            fullWidth
                            margin="dense"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Controller
                        name="prefix"
                        control={control}
                        rules={{ required: 'Prefisso obbligatorio' }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Prefisso"
                            fullWidth
                            margin="dense"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Phone fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <Controller
                        name="phone"
                        control={control}
                        rules={{
                          required: 'Telefono obbligatorio',
                          pattern: {
                            value: /^[0-9]{8,}$/,
                            message: 'Minimo 8 cifre, solo numeri',
                          },
                        }}
                        render={({
                          field: { onChange, onBlur, value },
                          fieldState,
                        }) => (
                          <TextField
                            label="Telefono"
                            fullWidth
                            margin="dense"
                            value={value}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            onBlur={onBlur}
                            onChange={(e) => {
                              // Remove non-digits
                              const onlyNumbers = e.target.value.replace(/\D/g, '');
                              onChange(onlyNumbers);
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <SectionTitle variant="subtitle1" sx={{ mt: 2 }}>
                    <DirectionsCar /> Dati Veicolo
                  </SectionTitle>
                  
                  <Controller
                    name="licensePlate"
                    control={control}
                    rules={{ required: 'Targa obbligatoria' }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Targa Veicolo"
                        placeholder="Es. AB123CD"
                        fullWidth
                        margin="dense"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DirectionsCar color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </StyledPaper>
              ) : (
                <StyledPaper elevation={0}>
                  <SectionTitle variant="subtitle1">
                    <DirectionsCar /> Seleziona o Aggiungi Veicolo
                  </SectionTitle>

                  {userVehicles.length > 0 ? (
                    <>
                      <Controller
                        name="vehicleId"
                        control={control}
                        render={({ field, fieldState }) => (
                          <Autocomplete
                            {...field}
                            options={userVehicles}
                            getOptionLabel={(option) =>
                              option && option.licensePlate !== undefined
                                ? option.licensePlate
                                : ''
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            onChange={handleVehicleChange}
                            value={
                              userVehicles.find((v) => v.id === getValues('vehicleId')) ||
                              null
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Seleziona Veicolo Esistente"
                                fullWidth
                                margin="dense"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <DirectionsCar color="primary" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <DirectionsCar fontSize="small" sx={{ mr: 1 }} />
                                  <Typography variant="body2">
                                    {option.licensePlate}
                                    {option.carName && ` - ${option.carName}`}
                                  </Typography>
                                </Box>
                              </li>
                            )}
                          />
                        )}
                      />

                      <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
                          oppure
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>

                      <Controller
                        name="licensePlate"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Inserisci Nuova Targa"
                            placeholder="Es. AB123CD"
                            fullWidth
                            margin="dense"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || 'Inserisci una nuova targa per aggiungere un veicolo'}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Add color="primary" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </>
                  ) : (
                    <>
                      <InfoBox>
                        <Typography variant="body2" color="info.main" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Info fontSize="small" sx={{ mr: 1 }} />
                          Questo cliente non ha veicoli registrati. Inserisci una targa per procedere.
                        </Typography>
                      </InfoBox>

                      <Controller
                        name="licensePlate"
                        control={control}
                        rules={{ required: 'Targa obbligatoria' }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Targa Veicolo"
                            placeholder="Es. AB123CD"
                            fullWidth
                            margin="dense"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DirectionsCar color="primary" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </>
                  )}
                </StyledPaper>
              )}
            </>
          )}

          {/* Step 2: Booking Details */}
          {activeStep === 1 && (
            <>
              <SectionTitle variant="subtitle1">
                <Event /> Periodo di Sosta
              </SectionTitle>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <Controller
                    name="start"
                    control={control}
                    rules={{ validate: validateStartDate }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Data e Ora di Inizio"
                        fullWidth
                        margin="dense"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || ''}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonth color="primary" />
                            </InputAdornment>
                          ),
                          sx: {
                            cursor: 'pointer',
                            '& input': {
                              cursor: 'pointer',
                            },
                            '& input::-webkit-calendar-picker-indicator': {
                              opacity: 0,
                              position: 'absolute',
                              right: 0,
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '100%',
                              height: '100%',
                              cursor: 'pointer',
                            }
                          }
                        }}
                        onClick={(e) => {
                          // Force focus on the input to trigger the date picker
                          e.currentTarget.querySelector('input').focus();
                          e.currentTarget.querySelector('input').showPicker?.();
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <FormControl
                    fullWidth
                    margin="dense"
                    error={!!errors.duration}
                  >
                    <Controller
                      name="duration"
                      control={control}
                      rules={{ required: 'Durata obbligatoria' }}
                      render={({ field, fieldState }) => (
                        <>
                          <InputLabel id="duration-label">Durata</InputLabel>
                          <Select
                            {...field}
                            labelId="duration-label"
                            label="Durata"
                            startAdornment={
                              <InputAdornment position="start">
                                <Schedule color="primary" />
                              </InputAdornment>
                            }
                          >
                            {[...Array(14).keys()].map((day) => (
                              <MenuItem key={day + 1} value={day + 1}>
                                {day + 1} giorn{day + 1 > 1 ? 'i' : 'o'}
                              </MenuItem>
                            ))}
                          </Select>
                          {fieldState.error && (
                            <FormHelperText error>
                              {fieldState.error.message}
                            </FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              
              {stayPeriod && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mt: 1, 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    p: 1,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  }}
                >
                  <EventAvailable 
                    fontSize="small" 
                    color="success" 
                    sx={{ mr: 1 }} 
                  />
                  <Typography variant="body2" color="success.main">
                    Periodo sosta: <b>{stayPeriod}</b>
                  </Typography>
                </Box>
              )}

              <SectionTitle variant="subtitle1" sx={{ mt: 3 }}>
                <LocalParking /> Seleziona Piazzola
              </SectionTitle>
              
              <Controller
                name="pitch"
                control={control}
                rules={{ required: 'Piazzola obbligatoria' }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    options={allPitchesForDropdown}
                    getOptionLabel={(option) => {
                      if (!option || !option.friendlyName) return '';
                      
                      if (!option.isAvailable && option.conflictInfo) {
                        return `${option.friendlyName} - ${option.conflictInfo.availabilityText}`;
                      }
                      
                      return option.friendlyName;
                    }}
                    isOptionEqualToValue={(option, value) => 
                      option?.id === (typeof value === 'object' ? value?.id : undefined)
                    }
                    onChange={(event, value) => {
                      handlePitchChange(event, value);
                      field.onChange(value?.friendlyName || '');
                    }}
                    value={
                      allPitchesForDropdown.find((p) => p.id === getValues('pitchId')) ||
                      null
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleziona Piazzola"
                        fullWidth
                        margin="dense"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocalParking color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <LocalParking 
                              fontSize="small" 
                              sx={{ 
                                mr: 1, 
                                color: option.isAvailable ? 'success.main' : 'warning.main' 
                              }} 
                            />
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {option.friendlyName}
                              </Typography>
                              {!option.isAvailable && option.conflictInfo && (
                                <Typography variant="caption" color="warning.main">
                                  {option.conflictInfo.availabilityText}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <StyledChip 
                            label={`€ ${option.price}/giorno`} 
                            size="small" 
                          />
                        </Box>
                      </li>
                    )}
                  />
                )}
              />
              
              {/* Info message about automatic time adjustment */}
              {pitchesWithAvailability.some(p => !p.isAvailable) && watchStart && watchDuration && (
                <Alert 
                  severity="info" 
                  sx={{ mt: 2, borderRadius: 1 }}
                  icon={<Info fontSize="inherit" />}
                >
                  <Typography variant="body2">
                    Le piazzole con testo arancione sono occupate nel periodo selezionato. 
                    <strong> Selezionandole, l'orario di inizio verrà automaticamente aggiustato.</strong>
                  </Typography>
                </Alert>
              )}

              {allPitchesForDropdown.length === 0 && watchStart && watchDuration && (
                <Alert 
                  severity="warning" 
                  sx={{ mt: 2, borderRadius: 1 }}
                  icon={<Warning fontSize="inherit" />}
                >
                  Non ci sono piazzole disponibili per il periodo selezionato. Prova a cambiare la data o la durata.
                </Alert>
              )}

              <SectionTitle variant="subtitle1" sx={{ mt: 3 }}>
                <Group /> Numero Ospiti
              </SectionTitle>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Controller
                    name="adults"
                    control={control}
                    rules={{ required: 'Obbligatorio', min: 1 }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Adulti"
                        fullWidth
                        margin="dense"
                        type="number"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Group color="primary" />
                            </InputAdornment>
                          ),
                          inputProps: { min: 1 }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="childrens"
                    control={control}
                    rules={{ required: 'Obbligatorio', min: 0 }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Bambini (0-14)"
                        fullWidth
                        margin="dense"
                        type="number"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <ChildCare color="primary" />
                            </InputAdornment>
                          ),
                          inputProps: { min: 0 }
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              
              {/* Price summary */}
              {getValues('pricePerDay') > 0 && (
                <Box 
                  sx={{ 
                    mt: 3, 
                    p: 2, 
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Riepilogo Prezzo
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        Prezzo giornaliero:
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={500}>
                        € {getValues('pricePerDay')}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        Durata soggiorno:
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {getValues('duration')} giorn{getValues('duration') > 1 ? 'i' : 'o'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Totale:
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                        € {watchPrice || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          )}

          {/* Step 3: Payment */}
          {activeStep === 2 && (
            <>
              <SectionTitle variant="subtitle1">
                <Payment /> Metodo di Pagamento
              </SectionTitle>
              
              <Controller
                name="paymentType"
                control={control}
                defaultValue="contanti"
                rules={{ required: 'Metodo di pagamento obbligatorio' }}
                render={({ field, fieldState }) => (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <PaymentMethodButton
                          variant="outlined"
                          startIcon={<AttachMoney />}
                          onClick={() => setValue('paymentType', 'contanti')}
                          active={watchPaymentType === 'contanti'}
                          fullWidth
                        >
                          Contanti
                        </PaymentMethodButton>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <PaymentMethodButton
                          variant="outlined"
                          startIcon={<CreditCard />}
                          onClick={() => setValue('paymentType', 'carta')}
                          active={watchPaymentType === 'carta'}
                          fullWidth
                        >
                          Carta
                        </PaymentMethodButton>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <PaymentMethodButton
                          variant="outlined"
                          startIcon={<AccountBalance />}
                          onClick={() => setValue('paymentType', 'bonifico')}
                          active={watchPaymentType === 'bonifico'}
                          fullWidth
                        >
                          Bonifico
                        </PaymentMethodButton>
                      </Grid>
                    </Grid>
                    
                    {fieldState.error && (
                      <FormHelperText error sx={{ ml: 2 }}>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </Box>
                )}
              />
              
              <Box 
                sx={{ 
                  mt: 4, 
                  mb: 2,
                  p: 3, 
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Typography variant="h6" color="primary.main" gutterBottom fontWeight={600}>
                  Riepilogo Prenotazione
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Cliente
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Person fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={500}>
                          {getValues('firstName')} {getValues('lastName')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {getValues('prefix')}{getValues('phone')}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Veicolo
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DirectionsCar fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={500}>
                          {(() => {
                            // If vehicleId is set, find and show that vehicle's plate
                            const vehicleId = getValues('vehicleId');
                            if (vehicleId && userVehicles.length > 0) {
                              const selectedVehicle = userVehicles.find(v => v.id === vehicleId);
                              if (selectedVehicle) return selectedVehicle.licensePlate;
                            }
                            // Otherwise show the manually entered plate
                            return getValues('licensePlate') || 'N/A';
                          })()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Dettagli Soggiorno
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <LocalParking fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={500}>
                          Piazzola: {getValues('pitch')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Event fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          Dal: {formatDate(getValues('start'))}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Schedule fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          Durata: {getValues('duration')} giorn{getValues('duration') > 1 ? 'i' : 'o'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Group fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          Ospiti: {getValues('adults')} adulti, {getValues('childrens')} bambini
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Pagamento
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {getPaymentIcon(getValues('paymentType'))}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          Metodo: {
                            getValues('paymentType') === 'contanti' 
                              ? 'Contanti' 
                              : getValues('paymentType') === 'carta'
                                ? 'Carta' 
                                : 'Bonifico'
                          }
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Euro fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          Prezzo Totale: € {watchPrice || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              
              <Alert 
                severity="info" 
                icon={<Note fontSize="inherit" />}
                sx={{ mt: 2, borderRadius: 1 }}
              >
                Verificare che tutti i dati siano corretti prima di salvare la prenotazione.
              </Alert>
            </>
          )}
        </Box>
      </DialogContent>

      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        {activeStep > 0 ? (
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            variant="outlined"
          >
            Indietro
          </Button>
        ) : (
          <Button onClick={onClose} color="inherit">
            Annulla
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button
            endIcon={<ArrowForward />}
            onClick={handleNext}
            variant="contained"
            color="primary"
          >
            Avanti
          </Button>
        ) : (
          <ActionButton
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Check />}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Conferma Prenotazione'}
          </ActionButton>
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default BookingModal;